import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from "firebase/firestore";
import { updatePassword, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";

function ProfileSettings() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Delete account states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1: confirm, 2: password

  useEffect(() => {
    const fetchProfile = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setIsLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", uid);
        const docSnap = await getDoc(userRef);
        if (docSnap.exists()) {
          setProfile(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const validatePassword = () => {
    if (newPassword && newPassword.length < 6) {
      setMessage("❌ Password must be at least 6 characters long.");
      return false;
    }
    if (newPassword && newPassword !== confirmPassword) {
      setMessage("❌ Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validatePassword()) return;

    setIsSaving(true);
    setMessage("");
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const userRef = doc(db, "users", uid);

    try {
      // Update profile preferences
      await updateDoc(userRef, {
        newsletter: profile.newsletter || "off",
        notifications: profile.notifications || "off",
        updatedAt: new Date().toISOString()
      });

      // Update password if entered
      if (newPassword) {
        await updatePassword(auth.currentUser, newPassword);
        setNewPassword("");
        setConfirmPassword("");
      }

      setMessage("✅ Settings updated successfully!");
    } catch (err) {
      console.error("Error saving settings:", err);
      let errorMessage = "❌ Failed to save settings.";
      
      if (err.code === "auth/requires-recent-login") {
        errorMessage = "❌ Please log out and log back in to change your password.";
      } else if (err.code === "auth/weak-password") {
        errorMessage = "❌ Password is too weak. Please choose a stronger password.";
      }
      
      setMessage(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  // Delete account functions
  const handleDeleteAccount = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }

    if (!deletePassword) {
      setMessage("❌ Please enter your current password to delete your account.");
      return;
    }

    setIsDeleting(true);
    const user = auth.currentUser;
    
    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);
      
      // Delete from "users" collection by email
      const usersRef = collection(db, "users");
      const usersQuery = query(usersRef, where("email", "==", user.email));
      const usersSnapshot = await getDocs(usersQuery);
      
      const deletePromises = [];
      usersSnapshot.forEach((doc) => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
        console.log(`Deleted ${deletePromises.length} user document(s) from "users" collection`);
      } else {
        console.log("No user document found in 'users' collection with matching email");
      }
      
      // Delete from "profiles" collection by UID
      try {
        const profileRef = doc(db, "profiles", user.uid);
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          await deleteDoc(profileRef);
          console.log("Deleted user profile from 'profiles' collection");
        } else {
          console.log("No profile document found in 'profiles' collection with UID");
        }
      } catch (profileError) {
        console.error("Error deleting profile:", profileError);
        // Continue with deletion even if profile deletion fails
      }
      
      // Also check for profiles by email as backup
      try {
        const profilesRef = collection(db, "profiles");
        const profilesQuery = query(profilesRef, where("email", "==", user.email));
        const profilesSnapshot = await getDocs(profilesQuery);
        
        const profileDeletePromises = [];
        profilesSnapshot.forEach((doc) => {
          profileDeletePromises.push(deleteDoc(doc.ref));
        });
        
        if (profileDeletePromises.length > 0) {
          await Promise.all(profileDeletePromises);
          console.log(`Deleted ${profileDeletePromises.length} profile document(s) from "profiles" collection by email`);
        }
      } catch (profileEmailError) {
        console.error("Error deleting profiles by email:", profileEmailError);
        // Continue with deletion even if this fails
      }
      
      // Delete the user account from Firebase Auth
      await deleteUser(user);
      
      // Navigate to landing page after successful deletion
      navigate('/');
      
    } catch (error) {
      console.error("Error deleting account:", error);
      let errorMessage = "❌ Failed to delete account.";
      
      if (error.code === "auth/wrong-password") {
        errorMessage = "❌ Incorrect password. Please try again.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "❌ Too many failed attempts. Please try again later.";
      }
      
      setMessage(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeletePassword("");
    setMessage("");
  };

  const handleExportData = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const userRef = doc(db, "users", uid);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const userData = {
          ...docSnap.data(),
          email: auth.currentUser?.email,
          exportedAt: new Date().toISOString()
        };
        
        const dataStr = JSON.stringify(userData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `profile-data-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        URL.revokeObjectURL(url);
        setMessage("✅ Data exported successfully!");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setMessage("❌ Failed to export data.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Profile Settings</h2>
        <p className="text-gray-600 dark:text-gray-400">Manage your account information and preferences</p>
      </div>

      {/* Account Information (Read-only) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Information</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5">
              <span className="text-gray-900 dark:text-white">{profile.name || "Not specified"}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Contact support to change your name</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5">
              <span className="text-gray-900 dark:text-white">{profile.email || auth.currentUser?.email || "Not specified"}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Contact support to change your email</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Type</label>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5">
              <span className="text-gray-900 dark:text-white capitalize">{profile.role || "User"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Security Settings</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password (optional)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                {showPassword ? "👁️" : "🙈"}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave blank to keep current password</p>
          </div>

          {newPassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirm New Password</label>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              />
            </div>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preferences</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Newsletter Subscription</label>
            <select
              name="newsletter"
              value={profile.newsletter || "off"}
              onChange={handleChange}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              <option value="off">Unsubscribed</option>
              <option value="basic">Basic Updates</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Receive updates about new college lists and features</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Push Notifications</label>
            <select
              name="notifications"
              value={profile.notifications || "off"}
              onChange={handleChange}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              <option value="off">Disabled</option>
              <option value="on">Enabled</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Get notified about important updates and deadlines</p>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.includes("✅") 
            ? "bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700" 
            : "bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700"
        }`}>
          {message}
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-400 dark:disabled:bg-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:cursor-not-allowed flex items-center"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>

      {/* Account Actions */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Actions</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Export Data</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Download your profile and preferences data</p>
            </div>
            <button 
              onClick={handleExportData}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Export
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Delete Account</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Permanently remove your account and all data</p>
            </div>
            <button 
              onClick={() => setShowDeleteModal(true)}
              className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/40 mb-4">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {deleteStep === 1 ? "Delete Account" : "Confirm Deletion"}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {deleteStep === 1 
                  ? "Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data."
                  : "Please enter your current password to confirm account deletion."
                }
              </p>
            </div>

            {deleteStep === 2 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Password</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                />
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={resetDeleteModal}
                disabled={isDeleting}
                className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  deleteStep === 1 ? "Continue" : "Delete Account"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileSettings;